import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AuthService,
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterConfig,
  FilterOption,
  createDynamicFilterState,
} from '@card-software/library';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { MenuItem, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable, Subject, catchError, finalize, map, of, switchMap, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LocalHelperService } from '../../../services/local-helper.service';
import { EnumService } from '../../../services/enum.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import {
  PurchaseOrder,
  PurchaseOrderSearch,
  PurchaseOrderStatus,
  PurchaseOrderType,
} from '../../../models/order/purchase-order/purchase-order';
import { DispatchRequest, StatusDispatchRequest } from '../../../models/dispatch-request/dispatch-request';
import { DispatchRequestSearchCommand } from '../../../models/inventory/dispatch-request-search';
import { SupplyWarehouseDocumentType } from '../../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { globalSortFunction } from '../../../utils/global-sort.util';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { DispatchRequestProxyService } from '../../../proxies/inventory/dispatch-request.proxy.service';
import { ExternalRequisition, ExternalRequisitionSearchCommand } from '../../../models/order/external-requisition/external-requisition';
import { ExternalRequisitionProxyService } from '../../../proxies/order/external-requisition.proxy.service';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { DepartmentProxyService } from '../../../proxies/user-master';
import { Department } from '../../../models/user-master/department';
import { ExternalRequisitionStatusProxyService } from '../../../proxies/order/external-requisition-status.proxy.service';
import { GoodsReceiptProxyService } from '../../../proxies/inventory/goods-receipt.proxy.service';
import { debouncedServerSearch } from '@card-software/nexus-utilities';

interface DepartmentOption {
  id: string;
  name: string;
}

interface ProviderOption {
  id?: string;
  label: string;
}

@Component({
  selector: 'app-supply-warehouse-list',
  templateUrl: './supply-warehouse-list.component.html',
  styleUrl: './supply-warehouse-list.component.scss',
})
export class SupplyWarehouseListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private businessProxyService = inject(BusinessProxyService);
  private purchaseOrderService = inject(PurchaseOrderProxyService);
  private dispatchRequestService = inject(DispatchRequestProxyService);
  private requisitionService = inject(ExternalRequisitionProxyService);
  private departmentProxyService = inject(DepartmentProxyService);
  private requisitionStatusService = inject(ExternalRequisitionStatusProxyService);
  private goodsReceiptService = inject(GoodsReceiptProxyService);
  public localeHelperService = inject(LocalHelperService);

  private destroy$ = new Subject<void>();
  private providerSearch$ = new Subject<string>();

  // Location switch configuration
  readonly LOCATION_STORAGE_KEY = 'supply-warehouse-selected-location';
  private readonly locationProxyService = inject(LocationProxyService);

  locationOptions = signal<{ code: string; label: string; icon: string }[]>([]);

  selectedLocation = signal<string>(this.getStoredLocation());

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  section: { label: string; value: number }[] = [];
  purchaseOrderList = signal<PurchaseOrder[]>([]);
  receivedPurchaseOrderList = signal<PurchaseOrder[]>([]);
  dispatchRequestList = signal<DispatchRequest[]>([]);
  requisitionList = signal<ExternalRequisition[]>([]);
  selectedSection = new FormControl(1);
  statusesOrder = signal<EnumValues>({});
  statusesDispatchRequest = signal<EnumValues>({});
  statusesRequisition = signal<EnumValues>({});
  providers = signal<Business[]>([]);
  filteredProviders = signal<ProviderOption[]>([]);
  departments = signal<DepartmentOption[]>([]);
  filteredDepartments = signal<DepartmentOption[]>([]);
  private departmentsLoaded = signal(false);
  private requisitionStatusesLoaded = signal(false);
  // External Requisition status options. Populated from the status catalog exposed by the API task;
  // rendered as a hidden multiselect filter so the "Add Filter" dropdown appears on this tab.
  requisitionStatusOptions = signal<FilterOption[]>([]);
  documentItems: MenuItem[] = [];
  documentTypeSelected = 0;
  enumsReady = signal(false);
  canCreate = computed(() => AuthService.hasRole(UserGroup.SupplyWarehouseAdmin));
  /** Administrative orders (#5942) are listed only for role holders; the API hides them from everyone else. */
  canSeeAdministrative = computed(() => AuthService.hasRole(UserGroup.AdministrativePurchaseOrderAdmin));

  // Last-applied filter signatures per tab. The dynamic-filters library auto-applies on
  // autocomplete select/clear, which would re-run the search (and flash the loading overlay)
  // even when the filter value did not actually change. These guards skip those redundant runs.
  private lastPurchaseOrderFilterKey = '';
  private lastReceivedPurchaseOrderFilterKey = '';
  private lastExternalRequisitionFilterKey = '';

  // Filter configurations
  purchaseOrderFilterConfig = signal<DynamicFilterConfig | null>(null);
  receivedPurchaseOrderFilterConfig = signal<DynamicFilterConfig | null>(null);
  dispatchRequestFilterConfig = signal<DynamicFilterConfig | null>(null);
  externalRequisitionFilterConfig = signal<DynamicFilterConfig | null>(null);
  private readonly externalRequisitionFilterConfigWithOptions = computed(() =>
    this.departmentsLoaded() && this.requisitionStatusesLoaded() ? this.externalRequisitionFilterConfig() : null,
  );

  readonly purchaseOrderFilterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-purchase-orders-filters',
    config: this.purchaseOrderFilterConfig,
    defaults: this.purchaseOrderTypeDefaults(),
    paramPrefix: 'po.',
  });
  readonly receivedPurchaseOrderFilterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-received-purchase-orders-filters',
    config: this.receivedPurchaseOrderFilterConfig,
    defaults: this.purchaseOrderTypeDefaults(),
    paramPrefix: 'rpo.',
  });
  readonly dispatchRequestFilterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-dispatch-requests-filters',
    config: this.dispatchRequestFilterConfig,
    paramPrefix: 'dr.',
  });
  readonly externalRequisitionFilterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-external-requisitions-filters',
    config: this.externalRequisitionFilterConfigWithOptions,
    paramPrefix: 'er.',
  });

  private purchaseOrderFilters: DynamicFilterValues = {};
  private receivedPurchaseOrderFilters: DynamicFilterValues = {};
  private dispatchRequestFilters: DynamicFilterValues = {};
  private externalRequisitionFilters: DynamicFilterValues = {};

  // Pagination: Purchase Orders
  purchaseOrderCurrentPage = 1;
  purchaseOrderPageSize = 25;
  purchaseOrderTotalRecords = 0;

  // Pagination: Received Purchase Orders
  receivedPurchaseOrderCurrentPage = 1;
  receivedPurchaseOrderPageSize = 25;
  receivedPurchaseOrderTotalRecords = 0;

  // Computed filter options
  statusOrderOptions = computed<FilterOption[]>(() => {
    const statuses = this.statusesOrder();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  statusDispatchOptions = computed<FilterOption[]>(() => {
    const statuses = this.statusesDispatchRequest();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  readonly headerTitle = 'Suppl Warehouse Documents';

  constructor() {
    this.section = [
      {
        label: 'Purchase Orders',
        value: 1,
      },
      {
        label: 'Dispatch Requests',
        value: 2,
      },
      {
        label: 'External Requisition',
        value: 3,
      },
      {
        label: 'Completed Dispatches',
        value: 4,
      },
      {
        label: 'Received Purchase Orders',
        value: 5,
      },
      {
        label: 'Internal Returns',
        value: 6,
      },
      {
        label: 'Provider Returns',
        value: 7,
      },
    ];

    this.purchaseOrderFilterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchPurchaseOrdersWithFilters(filters));
    this.receivedPurchaseOrderFilterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchReceivedPurchaseOrdersWithFilters(filters));
    this.dispatchRequestFilterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchDispatchRequestsWithFilters(filters));
    this.externalRequisitionFilterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchExternalRequisitionsWithFilters(filters));

    // Load enums
    effect(
      () => {
        const statusesOrderValue = this.enumService.getEnum('PurchaseOrderStatus', 'order').values;
        if (Object.keys(statusesOrderValue).length > 0) {
          this.statusesOrder.set(statusesOrderValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const statusesDispatchValue = this.enumService.getEnum('StatusDispatchRequest', 'inventory').values;
        if (Object.keys(statusesDispatchValue).length > 0) {
          this.statusesDispatchRequest.set(statusesDispatchValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    // Initialize filter configs when translation and enums are ready
    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfigs();
        }
      },
      { allowSignalWrites: true },
    );

    //Add document types to documentItems
    this.documentItems = [
      {
        label: 'ProviderReturn (PR)',
        command: () => {
          this.documentTypeSelected = 2;
          this.redirectToDetails(SupplyWarehouseDocumentType.ProviderReturn, '');
        },
      },
    ];
  }

  ngOnInit() {
    this.initializeProviderSearch();
    this.loadConsumableWarehouses();
    this.loadDepartments();
    this.loadRequisitionStatuses();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfigs();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private getStoredLocation(): string {
    return localStorage.getItem(this.LOCATION_STORAGE_KEY) || '';
  }

  private storeLocation(locationCode: string): void {
    localStorage.setItem(this.LOCATION_STORAGE_KEY, locationCode);
  }

  private loadConsumableWarehouses(): void {
    this.locationProxyService.getConsumableWarehouses().subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.locationOptions.set(
            response.data.map((w) => ({
              code: w.locationCode,
              label: this.localeHelperService.getLocalizedValue(w.fullName ?? w.name),
              icon: 'pi pi-box',
            })),
          );
          // If no stored location or stored location not in options, select first
          const stored = this.getStoredLocation();
          const validOption = response.data.find((w) => w.locationCode === stored);
          if (!validOption) {
            this.selectedLocation.set(response.data[0].locationCode);
            this.storeLocation(response.data[0].locationCode);
            this.refreshAllDocuments();
          }
        }
      },
    });
  }

  selectedLocationLabel(): string {
    const option = this.locationOptions().find((o) => o.code === this.selectedLocation());
    return option?.label ?? this.selectedLocation();
  }

  onLocationToggle(): void {
    const options = this.locationOptions();
    if (options.length <= 1) return;
    const currentIndex = options.findIndex((o) => o.code === this.selectedLocation());
    const nextIndex = (currentIndex + 1) % options.length;
    this.selectedLocation.set(options[nextIndex].code);
    this.storeLocation(options[nextIndex].code);
    this.refreshAllDocuments();
  }

  private refreshAllDocuments(): void {
    this.searchPurchaseOrders();
    this.searchReceivedPurchaseOrders();
    this.searchDispatchRequests();
    this.searchExternalRequisitions();
  }

  private tryInitializeFilterConfigs(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializePurchaseOrderFilterConfig();
      this.initializeReceivedPurchaseOrderFilterConfig();
      this.initializeDispatchRequestFilterConfig();
      this.initializeExternalRequisitionFilterConfig();
    }
  }

  private initializePurchaseOrderFilterConfig(): void {
    const filteredProviders = this.filteredProviders;
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'purchaseOrderNumber',
          label: translate('Purchase_Order_PO_Number', 'PO Number'),
          placeholder: translate('Enter_PO_number', 'Enter PO number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'goodsReceiptNumber',
          label: translate('Goods_Receipt_Number', 'Goods Receipt Number'),
          placeholder: translate('Search_GRN_number', 'Search GRN number'),
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'provider',
          label: translate('Purchase_Order_Provider', 'Provider'),
          placeholder: translate('Select_provider', 'Select provider'),
          autocompleteConfig: {
            get suggestions() {
              return filteredProviders();
            },
            optionLabel: 'label',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterProviders(event),
            resolveById: (id: string) => this.resolveProvider(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        ...this.purchaseOrderTypeFilters(translate),
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Purchase_Order_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOrderOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.purchaseOrderFilterConfig.set(config);
  }

  private initializeReceivedPurchaseOrderFilterConfig(): void {
    const filteredProviders = this.filteredProviders;
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'purchaseOrderNumber',
          label: translate('Purchase_Order_PO_Number', 'PO Number'),
          placeholder: translate('Enter_PO_number', 'Enter PO number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'goodsReceiptNumber',
          label: translate('Goods_Receipt_Number', 'Goods Receipt Number'),
          placeholder: translate('Search_GRN_number', 'Search GRN number'),
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'provider',
          label: translate('Purchase_Order_Provider', 'Provider'),
          placeholder: translate('Select_provider', 'Select provider'),
          autocompleteConfig: {
            get suggestions() {
              return filteredProviders();
            },
            optionLabel: 'label',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterProviders(event),
            resolveById: (id: string) => this.resolveProvider(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        ...this.purchaseOrderTypeFilters(translate),
      ],
    };

    this.receivedPurchaseOrderFilterConfig.set(config);
  }

  private initializeDispatchRequestFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Dispatch_Request_Number', 'DR Number'),
          placeholder: translate('Search_DR_number', 'Search DR number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Dispatch_Request_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusDispatchOptions(),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'supplyRequestNumber',
          label: translate('Supply_Request_Number', 'Supply Request Number'),
          placeholder: translate('Search_supply_request_number', 'Search supply request number'),
          defaultVisible: false,
        },
      ],
    };

    this.dispatchRequestFilterConfig.set(config);
  }

  private initializeExternalRequisitionFilterConfig(): void {
    const filteredDepartments = this.filteredDepartments;
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Requisition_Number', 'Requisition Number'),
          placeholder: translate('Search_requisition_number', 'Search requisition number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'department',
          label: translate('Common_Department', 'Department'),
          placeholder: translate('Select_department', 'Select department'),
          autocompleteConfig: {
            get suggestions() {
              return filteredDepartments();
            },
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterDepartments(event),
            resolveById: (id: string) => of(this.departments().find((department) => department.id === id) ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Common_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.requisitionStatusOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.externalRequisitionFilterConfig.set(config);
  }

  private resolveProvider(id: string): Observable<ProviderOption | null> {
    return this.businessProxyService.getById(id).pipe(
      map((response) => (response.data ? this.toProviderOption(response.data) : null)),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  filterProviders(event: AutoCompleteCompleteEvent): void {
    this.providerSearch$.next(event.query);
  }

  private initializeProviderSearch(): void {
    // This screen was the hand-written prototype for the pattern; it now uses the shared operator.
    // Dropping its distinctUntilChanged is deliberate — a repeated query has to emit again, or the
    // host's loading flag never clears and the field stops responding after opening the panel twice.
    this.providerSearch$
      .pipe(
        debouncedServerSearch((query) => this.searchProviders(query), { debounceMs: 500 }),
        takeUntil(this.destroy$),
      )
      .subscribe((providers) => {
        this.providers.set(
          _.uniqBy([...this.providers(), ...providers], (provider) => provider.id ?? `${provider.name}|${provider.code ?? ''}`),
        );
        this.filteredProviders.set(providers.map((provider) => this.toProviderOption(provider)));
      });
  }

  private searchProviders(query: string): Observable<Business[]> {
    // The dropdown arrow searches with an empty query, which has to list what is already loaded.
    if (!query) return of(this.providers());

    const businessTypes = [BusinessEntityType.Provider, BusinessEntityType.InternalCompany];

    // Single request that matches by name OR code (matchAny=true) instead of two parallel calls.
    return this.businessProxyService
      .search({ name: query, code: query, businessTypes }, 1, 25, undefined, -1, true)
      .pipe(map((response) => response?.data?.items ?? []));
  }

  /**
   * Builds an autocomplete option whose label includes the provider code, e.g. "Acme (P-001)".
   * This lets the user search by code (the label is what PrimeNG matches with forceSelection) and
   * see the code in the suggestion list, while `optionValue: 'id'` keeps storing the provider id.
   */
  private toProviderOption(provider: Business): ProviderOption {
    const label = provider.code ? `${provider.name} (${provider.code})` : provider.name;
    return { id: provider.id, label };
  }

  private loadDepartments(): void {
    this.departmentProxyService.getAll().subscribe({
      next: (response) => {
        const options: DepartmentOption[] = (response.data ?? []).map((dep: Department) => ({
          id: dep.id,
          name: this.localeHelperService.getLocalizedValue(dep.name),
        }));
        this.departments.set(options);
        this.filteredDepartments.set(options);
        this.departmentsLoaded.set(true);
      },
      error: (error) => {
        console.error(error);
        this.departments.set([]);
        this.filteredDepartments.set([]);
        this.departmentsLoaded.set(true);
      },
    });
  }

  getDepartmentName(departmentReference?: string): string {
    if (!departmentReference) return '-';
    const dept = this.departments().find((d) => d.id === departmentReference);
    return dept?.name || departmentReference;
  }

  private loadRequisitionStatuses(): void {
    this.requisitionStatusService.getAll().subscribe({
      next: (response) => {
        const options: FilterOption[] = (response.data ?? [])
          .filter((status) => !!status.id)
          .map((status) => ({
            value: status.id,
            label: this.localeHelperService.getLocalizedValue(status.displayName),
          }));
        this.requisitionStatusOptions.set(options);
        this.requisitionStatusesLoaded.set(true);
      },
      error: (error) => {
        console.error(error);
        this.requisitionStatusOptions.set([]);
        this.requisitionStatusesLoaded.set(true);
      },
    });
  }

  filterDepartments(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.departments().filter((dep) => dep.name?.toLowerCase().includes(query));
    this.filteredDepartments.set(filtered);
  }

  /**
   * Navega al componente de detalle según el tipo de documento
   * @param documentType Tipo de documento (GRN, DR, MI, IR, PR)
   * @param documentId ID del documento a mostrar
   */
  redirectToDetails(documentType: SupplyWarehouseDocumentType, documentId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: documentType,
        id: documentId,
      },
    });
  }

  /**
   * Navega a GRN (Goods Receipt Note) desde Purchase Order
   */
  navigateToGRN(purchaseOrderId: string): void {
    this.redirectToDetails(SupplyWarehouseDocumentType.GoodsReceiptNote, purchaseOrderId);
  }

  /**
   * Navega a Dispatch Request para visualizar y editar
   * El DR no genera MI, se edita directamente
   */
  navigateToDispatchRequest(dispatchRequestId: string): void {
    this.redirectToDetails(SupplyWarehouseDocumentType.DispatchRequest, dispatchRequestId);
  }

  /**
   * Navega a Material Issue desde Internal Requisition
   * NOTA: Material Issue se genera desde Requisition, NO desde Dispatch Request
   */
  navigateToMaterialIssueFromRequisition(requisitionId: string): void {
    this.redirectToDetails(SupplyWarehouseDocumentType.MaterialIssue, requisitionId);
  }
  //Purchase Order
  searchPurchaseOrders() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.purchaseOrderFilters;
    const goodsReceiptNumber = formValues.goodsReceiptNumber?.trim();

    // PurchaseOrder has no back-reference to its GRN, and the PO search API has no goodsReceiptNumber
    // param. Resolve the GRN number to its PO number first (GoodsReceipt.initiator -> PurchaseOrder),
    // then run the normal paginated PO search filtering by that PO number.
    if (goodsReceiptNumber) {
      this.startLoading();
      this.resolveGrnToPurchaseOrderNumber(goodsReceiptNumber)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (poNumber) => {
            if (!poNumber) {
              this.purchaseOrderList.set([]);
              this.purchaseOrderTotalRecords = 0;
              return;
            }
            this.runPurchaseOrderSearch(formValues, poNumber);
          },
          error: (error) => {
            console.error(error);
            this.purchaseOrderList.set([]);
            this.purchaseOrderTotalRecords = 0;
          },
        });
      return;
    }

    this.runPurchaseOrderSearch(formValues, formValues.purchaseOrderNumber?.trim());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private runPurchaseOrderSearch(formValues: any, purchaseOrderNumber?: string) {
    const providerId = typeof formValues.provider === 'string' ? formValues.provider : formValues.provider?.id;

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const command: PurchaseOrderSearch = {
      types: this.purchaseOrderTypesForSearch(formValues.type),
      purchaseOrderNumber: purchaseOrderNumber || undefined,
      providerReference: providerId || undefined,
      status: statuses && statuses.length > 0 ? statuses : [PurchaseOrderStatus.Approved, PurchaseOrderStatus.PartiallyReceived],
      destinationLocationCodes: [this.selectedLocation()],
    };

    this.startLoading();
    this.purchaseOrderService
      .search(command, this.purchaseOrderCurrentPage, this.purchaseOrderPageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.purchaseOrderList.set(response.data?.items ?? []);
          this.purchaseOrderTotalRecords = response.data?.totalCount || 0;
        },
        error: (error) => {
          console.error(error);
          this.purchaseOrderList.set([]);
          this.purchaseOrderTotalRecords = 0;
        },
      });
  }

  /**
   * Resolves a Goods Receipt (GRN) number to the number of the PurchaseOrder it references.
   * GoodsReceipt.initiator -> PurchaseOrder; returns null when the GRN or its PO cannot be resolved.
   */
  private resolveGrnToPurchaseOrderNumber(goodsReceiptNumber: string): Observable<string | null> {
    return this.goodsReceiptService.search({ number: goodsReceiptNumber }).pipe(
      switchMap((response) => {
        const grn = response.data?.find((g) => g.number === goodsReceiptNumber) ?? response.data?.[0];
        const poId = grn?.initiator?.reference;
        if (!poId) {
          return of(null);
        }
        return this.purchaseOrderService.getById(poId).pipe(map((po) => po.data?.purchaseOrderNumber ?? null));
      }),
      catchError(() => of(null)),
    );
  }

  // Normalized signature of the filter values that actually affect the search, used to skip
  // redundant applies triggered by the library on autocomplete select/clear.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildFilterKey(filters: any): string {
    const providerId = typeof filters.provider === 'string' ? filters.provider : filters.provider?.id;
    const departmentId = typeof filters.department === 'string' ? filters.department : filters.department?.id;
    const statusesRaw = filters.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];
    return JSON.stringify({
      number: (filters.number ?? '').toString().trim(),
      purchaseOrderNumber: (filters.purchaseOrderNumber ?? '').toString().trim(),
      goodsReceiptNumber: (filters.goodsReceiptNumber ?? '').toString().trim(),
      provider: providerId ?? '',
      department: departmentId ?? '',
      statuses: [...statuses].sort(),
      // The supply warehouse Type filter (#5942); without it a type-only change looked like no change at all.
      type: typeof filters.type === 'object' && filters.type !== null ? filters.type.value : (filters.type ?? ''),
    });
  }

  private searchPurchaseOrdersWithFilters(filters: DynamicFilterValues): void {
    const key = this.buildFilterKey(filters);
    if (key === this.lastPurchaseOrderFilterKey) {
      return;
    }
    this.lastPurchaseOrderFilterKey = key;
    this.purchaseOrderFilters = filters;
    this.purchaseOrderCurrentPage = 1;
    this.searchPurchaseOrders();
  }

  onPurchaseOrderPageChange(event: PaginatorState): void {
    this.purchaseOrderCurrentPage = (event.page || 0) + 1;
    this.purchaseOrderPageSize = event.rows || this.purchaseOrderPageSize;
    this.searchPurchaseOrders();
  }

  onTablePurchaseOrderSort(event: SortEvent): void {
    globalSortFunction<PurchaseOrder>(event, ['purchaseOrderNumber']);
  }

  //Received Purchase Orders
  searchReceivedPurchaseOrders() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.receivedPurchaseOrderFilters;
    const goodsReceiptNumber = formValues.goodsReceiptNumber?.trim();

    // Same GRN -> PO number resolution as the Purchase Orders tab (see resolveGrnToPurchaseOrderNumber).
    if (goodsReceiptNumber) {
      this.startLoading();
      this.resolveGrnToPurchaseOrderNumber(goodsReceiptNumber)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (poNumber) => {
            if (!poNumber) {
              this.receivedPurchaseOrderList.set([]);
              this.receivedPurchaseOrderTotalRecords = 0;
              return;
            }
            this.runReceivedPurchaseOrderSearch(formValues, poNumber);
          },
          error: (error) => {
            console.error(error);
            this.receivedPurchaseOrderList.set([]);
            this.receivedPurchaseOrderTotalRecords = 0;
          },
        });
      return;
    }

    this.runReceivedPurchaseOrderSearch(formValues, formValues.purchaseOrderNumber?.trim());
  }

  /** Standard always; Administrative too when the user holds the role (#5942). */
  private purchaseOrderTypesForUser(): PurchaseOrderType[] {
    return this.canSeeAdministrative() ? [PurchaseOrderType.Standard, PurchaseOrderType.Administrative] : [PurchaseOrderType.Standard];
  }

  /** Role holders open the supply warehouse on their own Administrative orders; they can clear it to see both. */
  private purchaseOrderTypeDefaults(): DynamicFilterValues {
    return this.canSeeAdministrative() ? { type: PurchaseOrderType.Administrative } : {};
  }

  /** The Type filter exists only for role holders: everyone else sees Standard alone, so there is nothing to pick. */
  private purchaseOrderTypeFilters(translate: (key: string, fallback: string) => string): FilterConfig[] {
    if (!this.canSeeAdministrative()) {
      return [];
    }
    return [
      {
        type: 'select',
        formControlName: 'type',
        label: translate('Purchase_Order_Type', 'Order Type'),
        placeholder: translate('Select_type', 'Select type'),
        options: [
          { value: PurchaseOrderType.Standard, label: translate('Purchase_Order_Standard', 'Standard') },
          { value: PurchaseOrderType.Administrative, label: translate('Purchase_Order_Administrative', 'Administrative') },
        ],
        showClear: true,
        defaultVisible: true,
      },
    ];
  }

  /** The picked type when it is one the user may see; otherwise every type they may see. */
  private purchaseOrderTypesForSearch(selected: unknown): PurchaseOrderType[] {
    const allowed = this.purchaseOrderTypesForUser();
    const raw = typeof selected === 'object' && selected !== null ? (selected as FilterOption).value : selected;
    const type = raw === null || raw === undefined || raw === '' ? null : (Number(raw) as PurchaseOrderType);
    return type !== null && allowed.includes(type) ? [type] : allowed;
  }

  isAdministrativePurchaseOrder(po: PurchaseOrder): boolean {
    return po.type === PurchaseOrderType.Administrative;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private runReceivedPurchaseOrderSearch(formValues: any, purchaseOrderNumber?: string) {
    const providerId = typeof formValues.provider === 'string' ? formValues.provider : formValues.provider?.id;

    const command: PurchaseOrderSearch = {
      types: this.purchaseOrderTypesForSearch(formValues.type),
      purchaseOrderNumber: purchaseOrderNumber || undefined,
      providerReference: providerId || undefined,
      status: [PurchaseOrderStatus.Received],
      destinationLocationCodes: [this.selectedLocation()],
    };

    this.startLoading();
    this.purchaseOrderService
      .search(command, this.receivedPurchaseOrderCurrentPage, this.receivedPurchaseOrderPageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.receivedPurchaseOrderList.set(response.data?.items ?? []);
          this.receivedPurchaseOrderTotalRecords = response.data?.totalCount || 0;
        },
        error: (error) => {
          console.error(error);
          this.receivedPurchaseOrderList.set([]);
          this.receivedPurchaseOrderTotalRecords = 0;
        },
      });
  }

  private searchReceivedPurchaseOrdersWithFilters(filters: DynamicFilterValues): void {
    const key = this.buildFilterKey(filters);
    if (key === this.lastReceivedPurchaseOrderFilterKey) {
      return;
    }
    this.lastReceivedPurchaseOrderFilterKey = key;
    this.receivedPurchaseOrderFilters = filters;
    this.receivedPurchaseOrderCurrentPage = 1;
    this.searchReceivedPurchaseOrders();
  }

  onReceivedPurchaseOrderPageChange(event: PaginatorState): void {
    this.receivedPurchaseOrderCurrentPage = (event.page || 0) + 1;
    this.receivedPurchaseOrderPageSize = event.rows || this.receivedPurchaseOrderPageSize;
    this.searchReceivedPurchaseOrders();
  }

  onTableReceivedPurchaseOrderSort(event: SortEvent): void {
    globalSortFunction<PurchaseOrder>(event, ['purchaseOrderNumber']);
  }

  /**
   * Navega a GRN (Goods Receipt Note) desde Purchase Order recibido en modo solo lectura
   */
  navigateToReceivedPurchaseOrder(purchaseOrderId: string): void {
    this.redirectToDetails(SupplyWarehouseDocumentType.GoodsReceiptNote, purchaseOrderId);
  }

  //Dispatch Request
  searchDispatchRequests() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.dispatchRequestFilters;
    const number = formValues.number?.trim();
    const supplyRequestNumber = formValues.supplyRequestNumber?.trim();

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const command: DispatchRequestSearchCommand = {
      number: number || undefined,
      locationCode: this.selectedLocation(),
      status: statuses && statuses.length > 0 ? statuses[0] : number || supplyRequestNumber ? undefined : StatusDispatchRequest.Pending,
      supplyRequestNumber: supplyRequestNumber || undefined,
    };

    this.startLoading();
    this.dispatchRequestService
      .search(command)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.dispatchRequestList.set(response.data?.items ?? []);
        },
        error: (error) => {
          console.error(error);
          this.dispatchRequestList.set([]);
        },
      });
  }

  private searchDispatchRequestsWithFilters(filters: DynamicFilterValues): void {
    this.dispatchRequestFilters = filters;
    this.searchDispatchRequests();
  }

  onTableDispatchRequestSort(event: SortEvent): void {
    globalSortFunction<DispatchRequest>(event, ['number']);
  }

  //External Requisitions
  searchExternalRequisitions() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.externalRequisitionFilters;
    const number = formValues.number?.trim();
    const departmentId = typeof formValues.department === 'string' ? formValues.department : formValues.department?.id;

    const statusesRaw = formValues.statuses;
    const statusReferences = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const command: ExternalRequisitionSearchCommand = {
      number: number || undefined,
      // The Order API filters by a single `statusReference`. When the user picks status(es),
      // override the default with the (first) selection; otherwise keep the tab's default status.
      // `statusReferences` is sent for forward-compatibility once the API supports multi-status.
      statusReference: statusReferences.length > 0 ? statusReferences[0] : '68d3171eec2bcb5a25d49b4c',
      statusReferences: statusReferences.length > 0 ? statusReferences : undefined,
      departmentReference: departmentId || undefined,
      destinationLocationCode: this.selectedLocation(),
    };

    this.startLoading();
    this.requisitionService
      .search(command)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const allRequisitions = response.data?.items ?? [];
          // Filter to show only children requisitions (those whose initiator is another ExternalRequisition)
          // and requisitions with no initiator (null/undefined)
          const filteredRequisitions = allRequisitions.filter(
            (req) => !_.isNil(req?.parentRequisitionReference) && !_.isNil(req?.initiator),
          );
          this.requisitionList.set(filteredRequisitions);
        },
        error: (error) => {
          console.error(error);
          this.requisitionList.set([]);
        },
      });
  }

  private searchExternalRequisitionsWithFilters(filters: DynamicFilterValues): void {
    const key = this.buildFilterKey(filters);
    if (key === this.lastExternalRequisitionFilterKey) {
      return;
    }
    this.lastExternalRequisitionFilterKey = key;
    this.externalRequisitionFilters = filters;
    this.searchExternalRequisitions();
  }

  onTableRequisitionSort(event: SortEvent): void {
    globalSortFunction<ExternalRequisition>(event, ['number']);
  }

  getDispatchStatusLabel(status: StatusDispatchRequest): string {
    const enumName = StatusDispatchRequest[status];
    const key = `Common_Status_${enumName}`;
    const result = this.translateService.instant(key);
    return result !== key ? result : this.statusesDispatchRequest()[status] || '';
  }

  getSpecificProvider(provider?: Business) {
    return provider ? `${provider.name} (${provider.code})` : '---------';
  }

  getSpecificBillTo(billTo?: Business) {
    return billTo ? billTo.name : '---------';
  }

  getSpecificShippingTo(shipTo?: Business) {
    return shipTo ? shipTo.name : '---------';
  }
}
